import {
    ArrowDropDownOutlined as ArrowDropDownOutlinedIcon,
    ClearOutlined as ClearOutlinedIcon,
} from '@mui/icons-material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

import { Caption, Headline } from '../Typography';

import type { FunctionComponent, MouseEventHandler, PropsWithChildren, ReactNode } from 'react';

const StyledHeader = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.primary.contrastText,
    borderBottom: `2px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    position: 'sticky',
    top: 0,
    zIndex: 1,
}));

type SideDrawerHeaderProps = { readonly label: string; readonly onClose?: MouseEventHandler };

const SideDrawerHeader: FunctionComponent<SideDrawerHeaderProps> = ({ label, onClose }) => (
    <StyledHeader>
        <Typography lineHeight={1} variant="h5">
            {label}
        </Typography>
        <IconButton
            sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
            }}
            onClick={onClose}
        >
            <ClearOutlinedIcon color="action" />
        </IconButton>
    </StyledHeader>
);

type SideDrawerProps = {
    readonly onClose?: () => void;
    readonly open: boolean;
    readonly title: string;
};

export const SideDrawer: FunctionComponent<PropsWithChildren<SideDrawerProps>> = ({
    children,
    onClose,
    open,
    title,
}) => {
    return (
        <MuiDrawer
            anchor="right"
            open={open}
            PaperProps={{
                sx: {
                    borderRadius: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    width: '320px',
                },
            }}
            onClose={onClose}
        >
            <SideDrawerHeader label={title} onClose={onClose} />
            {children}
        </MuiDrawer>
    );
};

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
    '.MuiAccordionSummary-content, .MuiAccordionSummary-content.Mui-expanded': { margin: 0 },
    borderBottom: `1px solid ${theme.palette.divider}`,
    margin: 'revert',
    padding: theme.spacing(2),
}));

const StyledAccordion = styled(Accordion)(() => ({
    '& .MuiCollapse-root': {
        "&, & *[class*='MuiCollapse-'], & *[class*='MuiAccordion-']": {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
        },
        transitionDuration: '0s !important',
    },
    '&.Mui-expanded': {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        margin: 0,
    },
}));

type SideDrawerContentProps = PropsWithChildren<{
    readonly subTitle?: string | undefined;
    readonly title: ReactNode;
}>;

export const SideDrawerContent: FunctionComponent<SideDrawerContentProps> = (props) => {
    const [isExpanded, setIsExpanded] = useState(true);

    const toggleView = (): void => setIsExpanded((prev) => !prev);

    return (
        <StyledAccordion elevation={0} expanded={isExpanded}>
            <StyledAccordionSummary expandIcon={<ArrowDropDownOutlinedIcon />} onClick={toggleView}>
                <div>
                    <Headline variant="h5">{props.title}</Headline>
                    <Caption type="light">{props.subTitle}</Caption>
                </div>
            </StyledAccordionSummary>
            <AccordionDetails
                sx={{
                    flex: 1,
                    p: 0,
                }}
            >
                {props.children}
            </AccordionDetails>
        </StyledAccordion>
    );
};
